import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Button, ConfigProvider, Skeleton, message } from "antd"
import {
  CheckCircleFilled,
  FileProtectOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  LockOutlined,
  ReloadOutlined,
} from "@ant-design/icons"
import { useSelector } from "react-redux"
import { jwtDecode } from "jwt-decode"
import dayjs from "dayjs"
import { translations } from "../../utils/Translation/staticTranslation"
import { SitePoliciesApi, SITE_POLICY_KEYS } from "../../Apis/sitePoliciesApi"
import { isSitePoliciesReadOnly } from "../../utils/modulePermissionSidebar"
import { getUserCookies } from "../../utils/Cookies"
import { sanitizePolicyHtml, isPolicyHtmlEmpty } from "../../utils/sitePolicyHtml"
import { getCreateRolesApiErrorMessage } from "../../utils/createRolesHelpers"
import PolicyEditor from "../../components/SitePolicies/PolicyEditor"
import "../../components/SitePolicies/SitePolicies.css"

const BRAND = "#FB6514"

// Only the content is edited here. The backend's title_en / title_ar are left
// untouched; the website falls back to its own page headings.
const EDITED_FIELDS = ["content_en", "content_ar"]

const EMPTY_POLICY = {
  content_en: "",
  content_ar: "",
  time_updated: null,
}

const POLICY_ICONS = {
  privacy_policy: <FileProtectOutlined />,
  terms_and_conditions: <FileTextOutlined />,
}

const LANGUAGES = ["en", "ar"]

const pickEditable = (row) =>
  EDITED_FIELDS.reduce((acc, field) => {
    acc[field] = row?.[field] ?? ""
    return acc
  }, {})

/** GET list may come back as a bare array or a paginated `{ results }`. */
const toPolicyMap = (data) => {
  const list = Array.isArray(data) ? data : data?.results || []
  const map = {}
  for (const key of SITE_POLICY_KEYS) {
    const row = list.find((r) => r?.key === key)
    map[key] = { ...EMPTY_POLICY, ...(row || {}), key }
  }
  return map
}

const changedFields = (saved, draft) => {
  const diff = {}
  for (const field of EDITED_FIELDS) {
    if ((draft[field] ?? "") !== (saved[field] ?? "")) diff[field] = draft[field]
  }
  return diff
}

/** "live" when both languages have content, "partial" for one, "empty" for none. */
const contentStatus = (row) => {
  const filled = ["en", "ar"].filter((c) => !isPolicyHtmlEmpty(row?.[`content_${c}`]))
  if (filled.length === 2) return "live"
  return filled.length ? "partial" : "empty"
}

const SitePolicies = () => {
  const user = useSelector((state) => state.user.user)
  const token = getUserCookies()?.token
  const decoded = token ? jwtDecode(token) : null
  const readOnly = isSitePoliciesReadOnly(user, decoded)

  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.sitePoliciesPage || {}
  const isRTL = lang === "ar"

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  // `saved` mirrors the server; `drafts` is what the admin is editing.
  const [saved, setSaved] = useState({})
  const [drafts, setDrafts] = useState({})
  const [saving, setSaving] = useState(false)
  const [activeKey, setActiveKey] = useState(SITE_POLICY_KEYS[0])
  const [activeLang, setActiveLang] = useState(lang === "ar" ? "ar" : "en")

  const fetchPolicies = useCallback(async () => {
    try {
      setLoading(true)
      setLoadError(false)
      const response = await SitePoliciesApi.getSitePolicies()
      const map = toPolicyMap(response?.data)
      setSaved(map)
      setDrafts(
        Object.fromEntries(
          Object.entries(map).map(([key, row]) => [key, pickEditable(row)])
        )
      )
    } catch (error) {
      console.error("Error fetching site policies:", error)
      setLoadError(true)
      message.error(
        translations[lang]?.sitePoliciesPage?.error_fetching ||
          "Could not load the pages"
      )
    } finally {
      setLoading(false)
    }
  }, [lang])

  useEffect(() => {
    fetchPolicies()
  }, [fetchPolicies])

  const dirtyKeys = useMemo(
    () =>
      SITE_POLICY_KEYS.filter(
        (key) =>
          saved[key] &&
          drafts[key] &&
          Object.keys(changedFields(saved[key], drafts[key])).length > 0
      ),
    [saved, drafts]
  )

  // Warn before closing the tab with unsaved edits.
  useEffect(() => {
    if (!dirtyKeys.length) return undefined
    const handler = (e) => {
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [dirtyKeys.length])

  const updateDraft = (field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [activeKey]: { ...prev[activeKey], [field]: value },
    }))
  }

  const handleDiscard = () => {
    setDrafts((prev) => ({ ...prev, [activeKey]: pickEditable(saved[activeKey]) }))
  }

  const handleSave = async () => {
    if (readOnly) return
    const key = activeKey
    const draft = drafts[key]
    const cleaned = {
      content_en: isPolicyHtmlEmpty(draft.content_en)
        ? ""
        : sanitizePolicyHtml(draft.content_en),
      content_ar: isPolicyHtmlEmpty(draft.content_ar)
        ? ""
        : sanitizePolicyHtml(draft.content_ar),
    }
    const payload = changedFields(saved[key], cleaned)
    if (!Object.keys(payload).length) {
      message.info(t.no_changes || "No changes to save")
      return
    }

    try {
      setSaving(true)
      const response = await SitePoliciesApi.updateSitePolicy(key, payload)
      const row = {
        ...EMPTY_POLICY,
        ...saved[key],
        ...cleaned,
        ...(response?.data || {}),
        key,
      }
      setSaved((prev) => ({ ...prev, [key]: row }))
      setDrafts((prev) => ({ ...prev, [key]: pickEditable(row) }))
      message.success(t.save_success || "Page saved")
    } catch (error) {
      console.error("Error saving site policy:", error)
      message.error(
        getCreateRolesApiErrorMessage(error, t.save_error || "Could not save the page")
      )
    } finally {
      setSaving(false)
    }
  }

  const formatUpdated = (value) =>
    value ? dayjs(value).format("DD MMM YYYY, HH:mm") : t.never_saved || "Never saved"

  const statusLabel = {
    live: t.status_live || "Live",
    partial: t.status_partial || "Partially filled",
    empty: t.status_empty || "Not set up",
  }

  // Same tab row as the Referrals page (.referrals-tabs).
  const renderTabs = () => (
    <div className="site-policies-tabs" role="tablist">
      {SITE_POLICY_KEYS.map((key) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={key === activeKey}
          className={`site-policies-tab-btn ${key === activeKey ? "active" : ""}`}
          onClick={() => setActiveKey(key)}
        >
          <span className="site-policies-tab-icon">{POLICY_ICONS[key]}</span>
          {t[key] || key}
          {dirtyKeys.includes(key) ? (
            <span className="site-policy-unsaved-dot" title={t.unsaved_changes} />
          ) : null}
        </button>
      ))}
    </div>
  )

  const renderEditorPanel = () => {
    const row = saved[activeKey] || EMPTY_POLICY
    const draft = drafts[activeKey] || {}
    const isDirty = dirtyKeys.includes(activeKey)
    const status = contentStatus(row)
    const dir = activeLang === "ar" ? "rtl" : "ltr"
    const contentField = `content_${activeLang}`
    const langMissing = isPolicyHtmlEmpty(draft[contentField])

    return (
      <section className="site-policy-panel">
        <header className="site-policy-panel-header">
          <div className="site-policy-panel-heading">
            <div className="site-policy-panel-title-row">
              <h2 className="site-policy-panel-title">{t[activeKey] || activeKey}</h2>
              <span className={`site-policy-status site-policy-status-${status}`}>
                <span className="site-policy-status-dot" />
                {statusLabel[status]}
              </span>
            </div>
            <div className="site-policy-panel-meta">
              {isDirty ? (
                <span className="site-policy-unsaved-pill">
                  <span className="site-policy-unsaved-dot" />
                  {t.unsaved_changes || "Unsaved changes"}
                </span>
              ) : (
                <span>
                  {t.last_updated || "Last updated"} · {formatUpdated(row.time_updated)}
                </span>
              )}
            </div>
          </div>

          {readOnly ? (
            <span className="site-policy-readonly-pill">
              <LockOutlined /> {t.read_only}
            </span>
          ) : (
            <div className="site-policy-actions">
              {isDirty ? (
                <Button type="text" onClick={handleDiscard} disabled={saving}>
                  {t.discard || "Discard"}
                </Button>
              ) : null}
              <Button
                type="primary"
                className="site-policy-primary-btn"
                loading={saving}
                disabled={!isDirty}
                onClick={handleSave}
              >
                {saving ? t.saving || "Saving..." : t.save || "Save changes"}
              </Button>
            </div>
          )}
        </header>

        <div className="site-policy-lang-switch" role="tablist">
          {LANGUAGES.map((code) => {
            const filled = !isPolicyHtmlEmpty(draft[`content_${code}`])
            return (
              <button
                key={code}
                type="button"
                role="tab"
                aria-selected={activeLang === code}
                className={`site-policy-lang-btn ${activeLang === code ? "is-active" : ""}`}
                onClick={() => setActiveLang(code)}
              >
                <span className="site-policy-lang-code">{code.toUpperCase()}</span>
                <span className="site-policy-lang-name">{t[`tab_${code}`]}</span>
                {filled ? (
                  <CheckCircleFilled className="site-policy-lang-check" />
                ) : (
                  <span className="site-policy-lang-missing">{t.lang_empty || "Empty"}</span>
                )}
              </button>
            )
          })}
        </div>

        {langMissing ? (
          <div className="site-policy-note">
            <InfoCircleOutlined />
            <span>{t[`lang_missing_${activeLang}`]}</span>
          </div>
        ) : null}

        <div className="site-policy-field">
          <span className="site-policy-label">{t.content_label || "Content"}</span>
          <PolicyEditor
            key={`${activeKey}-${activeLang}`}
            dir={dir}
            readOnly={readOnly}
            value={draft[contentField] || ""}
            previewTitle={
              translations[activeLang]?.sitePoliciesPage?.[activeKey] || ""
            }
            previewSubtitle={
              translations[activeLang]?.sitePoliciesPage?.[
                `${activeKey}_subtitle`
              ] || []
            }
            placeholder={t[`content_placeholder_${activeLang}`]}
            labels={{
              visual: t.editor_visual,
              preview: t.editor_preview,
              html: t.editor_html,
              words: t.words,
              previewEmpty: t.preview_empty,
            }}
            onChange={(html) => updateDraft(contentField, html)}
          />
        </div>
      </section>
    )
  }

  return (
    <ConfigProvider
      direction={isRTL ? "rtl" : "ltr"}
      theme={{ token: { colorPrimary: BRAND, borderRadius: 8 } }}
    >
      <div className="site-policies-page" dir={isRTL ? "rtl" : "ltr"}>
        <div className="site-policies-page-header">
          <h1 className="site-policies-page-title">{t.title || "Policies & Privacy"}</h1>
          <p className="site-policies-page-subtitle">{t.subtitle}</p>
        </div>

        {loading ? (
          <div className="site-policy-panel">
            <Skeleton active paragraph={{ rows: 12 }} />
          </div>
        ) : loadError ? (
          <div className="site-policies-error">
            <p>{t.error_fetching}</p>
            <Button icon={<ReloadOutlined />} onClick={fetchPolicies}>
              {t.retry || "Retry"}
            </Button>
          </div>
        ) : (
          <>
            {renderTabs()}
            {renderEditorPanel()}
          </>
        )}
      </div>
    </ConfigProvider>
  )
}

export default SitePolicies
